import React, { useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { createColumnHelper } from "@tanstack/react-table";

function EntityPropertySaleSheetList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  salesheetid,
  user,
  validateRelatedRecord,
  propertykey,
  propertyid,
  tableProfile,
}) {
  const dataSet = "propertySaleSheets";
  const dataSetPrimaryKey = "property_sale_sheet_id";

  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const tableData = Array.isArray(data) ? data : [];
  const columnHelper = createColumnHelper();

  const createId = useMemo(
    () => createElementIdFactory("entity-property-sale-sheet-list"),
    []
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId, controlType || "table"),
    [createId, controlType]
  );

  const isEntitySaleSheetsProfile = tableProfile === "entity-salesheets" || controlType === "table3";
  const resolvedPageSize = isEntitySaleSheetsProfile
    ? PAGE_SIZES.entityDetailSaleSheetsTab
    : PAGE_SIZES.propertySaleSheetsTab;

  function updateRowData(rowIndex, columnId, value) {
    const localData = [...tableData];
    if (!localData[rowIndex]) {
      return;
    }

    localData[rowIndex] = {
      ...localData[rowIndex],
      [columnId]: columnId === "property_key" && typeof value === "string" ? value.toUpperCase() : value,
      modified: true,
    };

    updateTableState(localData, dataSet);
  }

  async function handleSave({ row, column, value }) {
    if (column.id !== "property_key") {
      return true;
    }

    const record = {
      ...row.original,
      property_key: typeof value === "string" ? value.toUpperCase() : value,
      last_edited_by: user,
    };

    if (typeof validateRelatedRecord === "function") {
      const validation = validateRelatedRecord(record, dataSet);
      if (Array.isArray(validation) && validation[0] === false) {
        setErrorMessage(validation[1] || "Validation failed.");
        setShowErrorModal(true);
        return false;
      }
    }

    try {
      const result = await saveRecord(record, dataSet, dataSetPrimaryKey);
      if (result && result.result === false) {
        setErrorMessage(result.error_message || "Failed to save record.");
        setShowErrorModal(true);
        return false;
      }

      const localData = [...tableData];
      localData[row.index] = {
        ...localData[row.index],
        ...record,
        ...(result && typeof result === "object" ? result : {}),
        modified: false,
        new_record: false,
        ...(result?.id ? { [dataSetPrimaryKey]: result.id } : {}),
      };
      updateTableState(localData, dataSet);
      return true;
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save record.");
      setShowErrorModal(true);
      return false;
    }
  }

  async function handleDelete({ row }) {
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) {
      return false;
    }

    const record = {
      ...row.original,
      archived_date: new Date().toISOString(),
      last_edited_by: user,
    };

    try {
      const result = await saveRecord(record, dataSet, dataSetPrimaryKey);
      if (result && result.result === false) {
        setErrorMessage(result.error_message || "Failed to delete record.");
        setShowErrorModal(true);
        return false;
      }

      const localData = tableData.filter((_, index) => index !== row.index);
      updateTableState(localData, dataSet);
      return true;
    } catch (err) {
      setErrorMessage(err?.message || "Failed to delete record.");
      setShowErrorModal(true);
      return false;
    }
  }

  function addRecord() {
    const localData = [...tableData];
    localData.unshift({
      property_sale_sheet_id: "",
      property_id: propertyid || null,
      property_key: "",
      sale_sheet_id: salesheetid,
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
    });
    updateTableState(localData, dataSet);
  }

  const col_sale_sheet_id_link = columnHelper.accessor("sale_sheet_id", {
    id: "sale_sheet_id_link",
    header: "ID",
    cell: (e) => {
      const saleSheetId = e.getValue();
      const propertyKey = e.row?.original?.property_key;
      if (!saleSheetId || !propertyKey) {
        return "";
      }

      return <Link to={`/properties/${propertyKey}/salesheets/${saleSheetId}`}>{saleSheetId}</Link>;
    },
    size: 70,
  });

  const col_sale_sheet_id_readonly = columnHelper.accessor("sale_sheet_id", {
    id: "sale_sheet_id_readonly",
    header: "ID",
    size: 70,
  });

  const col_property_key_editable = columnHelper.accessor("property_key", {
    id: "property_key_editable",
    header: "Cityscope Ref",
    cell: EditableCell,
    size: 140,
    meta: { uppercase: true },
  });

  const col_property_key_link = columnHelper.accessor("property_key", {
    id: "property_key_link",
    header: "Cityscope Ref",
    cell: (e) => {
      const propertyKey = e.getValue();
      if (!propertyKey) {
        return "";
      }

      return <Link to={`../properties/${propertyKey}`}>{propertyKey}</Link>;
    },
    size: 140,
  });

  const col_current = columnHelper.accessor("current", {
    id: "current",
    header: "Current",
    cell: (e) => (
      <div style={{ textAlign: "center" }}>
        <Form.Check checked={Boolean(e.getValue())} type="checkbox" disabled />
      </div>
    ),
    size: 90,
    sortingFn: "basic",
  });

  const col_sale_status = columnHelper.accessor("sale_status", {
    id: "sale_status",
    header: "Sale Status",
    size: 220,
  });

  const col_agent_1 = columnHelper.accessor("agent_1", {
    id: "agent_1",
    header: "Agent 1",
    size: 180,
  });

  const col_agent_2 = columnHelper.accessor("agent_2", {
    id: "agent_2",
    header: "Agent 2",
    size: 180,
  });

  const col_next_follow_up = columnHelper.accessor("sale_follow_up_date", {
    id: "sale_follow_up_date",
    header: "Next Follow Up Date",
    cell: (e) => {
      const value = e.getValue();
      if (!value) {
        return "";
      }

      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) {
        return "";
      }

      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    },
    size: 160,
  });

  const col_last_follow_up = columnHelper.accessor("sale_status_date_checked", {
    id: "sale_status_date_checked",
    header: "Last Follow Up Date",
    cell: (e) => {
      const value = e.getValue();
      if (!value) {
        return "";
      }

      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) {
        return "";
      }

      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    },
    size: 160,
  });

  const col_delete = columnHelper.display({
    id: "delete",
    header: "",
    cell: (e) => (
      <Button type="button" size="sm" variant="danger" onClick={() => handleDelete({ row: e.row })}>
        Delete
      </Button>
    ),
    size: 90,
    enableSorting: false,
  });

  const columnsTable = [
    col_sale_sheet_id_link,
    col_property_key_editable,
    col_current,
    col_sale_status,
    col_agent_1,
    col_agent_2,
    col_next_follow_up,
    col_last_follow_up,
    col_delete,
  ];

  const columnsTable2 = [
    col_sale_sheet_id_readonly,
    col_property_key_editable,
    col_delete,
  ];

  const columnsTable3 = [
    col_property_key_link,
    col_current,
    col_sale_status,
    col_next_follow_up,
    col_last_follow_up,
  ];

  const columnsMap = {
    table: columnsTable,
    table1: columnsTable,
    table2: columnsTable2,
    table3: columnsTable3,
  };

  const activeColumns = columnsMap[controlType] || columnsTable;

  function renderComponent() {
    return (
      <div className="salessheets">
        {(controlType === "table" || controlType === "table2") && (
          <Row>
            <Col className="text-right">
              {controlType === "table" && propertykey && (
                <Link to={`/properties/${propertykey}/salesheets/new?propertyid=${propertyid || ""}`}>
                  <Button variant="secondary" size="sm">Add</Button>
                </Link>
              )}
              {controlType === "table2" && (
                <Button variant="secondary" size="sm" onClick={addRecord}>Add</Button>
              )}
            </Col>
          </Row>
        )}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            <GenericTable
              section={`property-sale-sheets-${controlType || "table"}`}
              columns={activeColumns}
              data={tableData}
              pageSize={resolvedPageSize}
              elementIds={tableElementIds}
              enablePagination={true}
              enforceMinRowsHeight={true}
              minVisibleRows={5}
              meta={{
                updateData: updateRowData,
                handleSave,
                handleDelete,
              }}
            />
          </Col>
        </Row>

        <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Uh Oh!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-danger mb-0" role="alert">
              {errorMessage}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowErrorModal(false)}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  return (
    <div className="EntityPropertySaleSheetList">
      {isAuthenticated ? renderComponent() : null}
    </div>
  );
}

export default EntityPropertySaleSheetList;